import { App, Modal, Tooltip } from 'antd';
import { unix } from 'dayjs';
import get from 'lodash-es/get';
import { useEffect, useState } from 'react';

import { billingActions } from '@store/subscription/subscription.slice';

import Icon from '@asset/check.icon.svg?react';
import { Button } from '@atom/button/button';
import { ButtonTypeEnum } from '@atom/button/button.enum';
import { Text, Title } from '@atom/typography/typography';
import { BillingTypeEnum } from '@enum/billing-type.enum';
import { ColorEnum } from '@enum/style/color.enum';
import {
  useCreateCheckoutSessionMutation,
  useCreateCustomerPortalMutation,
  useRequestSubscriptionMutation,
  useSwitchSubscriptionMutation,
} from '@hook/billing/billing.hooks';
import { useSubscription } from '@hook/billing/subscription.hook';
import { useMutation } from '@hook/common/use-mutation.hook';
import { useStoreSelector } from '@hook/store/store.hooks';
import { ProductInterface } from '@interface/billing/billing.interface';
import { isExist, isString } from '@util/data/is-exist';

import { ActiveText, Description, ItemTitle, ListItem, ListWrapper, ManageLink, Wrapper } from './plan-card.style';
import { getPlanButtonText, getSupport, titleUnlimited } from './plan-card.util';

interface PlanCardProps {
  product: ProductInterface;
  currentType: BillingTypeEnum;
  onManageLink: () => void;
}

const DisableTypeText = ({ onManageLink }: Pick<PlanCardProps, 'onManageLink'>) => (
  <Text color={ColorEnum.Black20}>
    First, cancel your current subscription and wait for it to expire before switching the payment period. To cancel
    your subscription, go to <ManageLink onClick={onManageLink}>manage subscription</ManageLink>.
  </Text>
);

const DisableDowngradeText = () => (
  <Text color={ColorEnum.Black20}>
    This subscription unavailable — you have more applications than the lower plan supports.
  </Text>
);

const DisableSubscriptionText = () => (
  <Text color={ColorEnum.Black20}>Please renew your current subscription before you upgrade or downgrade.</Text>
);

export const PlanCard = ({ product, currentType, onManageLink }: PlanCardProps) => {
  const { message } = App.useApp();
  const { data: subscription, isLoading: isLoadingSubscription } = useSubscription();
  const [createCheckoutSession, { isLoading: isLoadingSession, data: checkout }] = useCreateCheckoutSessionMutation();
  const { action: switchSubscription, state: changeSubscriptionState } = useMutation(
    useSwitchSubscriptionMutation(),
    billingActions,
    billingActions.setData,
  );
  const [isModalOpen, setModalOpen] = useState(false);
  const applicationLength = useStoreSelector((state) => Object.keys(state.applications.data).length);
  const [createCustomerPortal] = useCreateCustomerPortalMutation();
  const [request, requestSubscription] = useRequestSubscriptionMutation();

  const planId = get(subscription, ['plan', '_id'], get(subscription, ['plan', 'id'], ''));
  const cancelAt = get(subscription, ['cancelAt']);
  const platform = get(subscription, ['platform']);
  const planType = get(subscription, ['plan', 'type'], '');
  const isBasePlan = get(subscription, ['plan', 'isBasePlan']);
  const state = get(subscription, 'state', 'active');
  const checkoutUrl = get(checkout, ['data', 'url'], '');

  const prevPlan = get(subscription, 'plans', []).find((item) => item.id === product.id);
  const prevPlanId = get(prevPlan, 'id', null);
  const prevCancelAt = get(prevPlan, 'cancelAt', null);
  const prevBillingCycle = get(prevPlan, 'billingCycle', null);

  const onRenew = async () => {
    const result = await createCustomerPortal().unwrap();
    window.location.href = result.data.url;
  };

  const onSwitchPlan = () => {
    if (product.type === 'yearly') {
      return request(product.id);
    }
    if (isBasePlan || state !== 'active' || platform !== 'stripe') {
      return createCheckoutSession(product.id);
    }

    if (Number.isInteger(subscription.cancelAt) && (product.id === planId || prevPlan?.status === 'trialing')) {
      // Number.isInteger(prevCancelAt) || (product.id === planId && Number.isInteger(subscription.cancelAt))) {
      return onRenew();
    }

    setModalOpen(true);
  };

  useEffect(
    () =>
      void (
        requestSubscription.isSuccess &&
        message.success('Your request for an annual plan has been sent. Please expect an email from the administrator.')
      ),
    [requestSubscription.isSuccess],
  );

  useEffect(() => {
    if (isString(checkoutUrl)) {
      window.location.href = checkoutUrl;
    }
  }, [checkoutUrl]);

  useEffect(() => {
    if (changeSubscriptionState.isSuccess) {
      message.success('Your subscription has been changed');
    }
    if (changeSubscriptionState.isError || changeSubscriptionState.error) {
      console.log(changeSubscriptionState);

      message.error(
        get(
          changeSubscriptionState,
          ['error', 'data', 'error', 'message'],
          get(changeSubscriptionState, ['error', 'message']),
        ),
      );
    }
  }, [changeSubscriptionState.isError, changeSubscriptionState.error, changeSubscriptionState.isSuccess]);

  const isLoading = isLoadingSubscription || isLoadingSession || changeSubscriptionState.isLoading;

  const preOptions = [
    // { name: "Unified API's", value: product.limits.unifiedApis },
    { name: "Unified API's", value: titleUnlimited(-1) },
    { name: 'Applications', value: titleUnlimited(product.limits.applications) },
    { name: 'Integrations', value: titleUnlimited(product.limits.integrations) },
    { name: 'Consumers', value: titleUnlimited(product.limits.consumers) },
    { name: 'API calls/mo', value: titleUnlimited(product.limits.apiCalls) },
  ];

  const postOptions = [
    { name: 'Webhooks', value: 'Unlimited' },
    { name: 'Log retention', value: `${product.limits.logRetention} days` },
    { name: 'Support', value: getSupport(product.limits.hasSupport) },
    { name: 'Custom Field Mapping', value: product.limits.hasCustomFields ? 'Yes' : 'No' },
    { name: 'Single sign-on', value: '' },
  ];

  const hasTrialing = get(subscription, 'plans', []).some((plan: any) => plan.status === 'trialing');
  const shouldDisableDowngrade = applicationLength > product.limits.applications && product.limits.applications !== -1;
  const shouldDisableType = !isBasePlan && planType !== product.type && !Number.isInteger(subscription.cancelAt);
  const shouldDisableCurrent = product.id === planId && !Number.isInteger(subscription.cancelAt);
  const shouldDisableCancel = product.id !== planId && Number.isInteger(subscription.cancelAt);
  const shouldDisablePrev = hasTrialing && Number.isInteger(subscription.cancelAt) && product.id !== planId;
  // Number.isInteger(prevCancelAt) && prevBillingCycle !== prevCancelAt && product.id === prevPlanId;
  const shouldDisableUpgrade = shouldDisableType || shouldDisableDowngrade || shouldDisablePrev || shouldDisableCancel;
  const isRenew =
    (product.id === planId && Number.isInteger(subscription.cancelAt)) ||
    (Number.isInteger(prevCancelAt) && product.id === prevPlanId && prevBillingCycle === prevCancelAt);

  return (
    <>
      <Wrapper>
        <Title level={3} weight={400}>
          {product.name}
        </Title>
        <Description>{product.description}</Description>
        <Title level={5} mb={24} mt={24} weight={400}>
          €{(product.price / 100).toFixed(2)} / {currentType === BillingTypeEnum.Monthly ? 'm' : 'y'}
        </Title>
        <Tooltip
          title={
            shouldDisableType ? (
              <DisableTypeText onManageLink={onManageLink} />
            ) : shouldDisableDowngrade ? (
              <DisableDowngradeText />
            ) : shouldDisableCancel ? (
              <DisableSubscriptionText />
            ) : null
          }
        >
          <Button
            disabled={shouldDisableCurrent || shouldDisableUpgrade}
            loading={isLoading}
            type={ButtonTypeEnum.Primary}
            block
            onClick={onSwitchPlan}
          >
            {getPlanButtonText(product, subscription, isRenew, prevPlan)}
          </Button>
        </Tooltip>
        {product.id === planId && isExist(cancelAt) && (
          <ActiveText>Active to {unix(cancelAt).format('DD/MM/YYYY HH:mm')}</ActiveText>
        )}
        <ListWrapper>
          {[...preOptions, ...product.additionalOptions, ...postOptions].map((current, index) => (
            <ListItem key={index.toString()}>
              <Icon />
              <ItemTitle>{current.name}</ItemTitle>
              <Text level='m'>
                {typeof current.value === 'number'
                  ? new Intl.NumberFormat('en-IN').format(current.value)
                  : current.value}
              </Text>
            </ListItem>
          ))}
        </ListWrapper>
      </Wrapper>
      <Modal
        confirmLoading={isLoading}
        open={isModalOpen}
        width={640}
        title={
          <Title level={4} weight={400}>
            Change subscription plan
          </Title>
        }
        onCancel={() => setModalOpen(false)}
        onOk={() => {
          setModalOpen(false);
          switchSubscription(product.id);
        }}
      >
        <Text>
          {cancelAt
            ? `Please note that your subscription will begin on ${unix(cancelAt).format('DD/MM/YYYY HH:mm')}, and you will be charged on the same day.`
            : 'Just so you know — the change takes effect immediately, and any prepaid fees will be credited toward your new plan.'}
        </Text>
      </Modal>
    </>
  );
};
